import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Menu,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import {
  playerHandleSuffix,
  authorLabel,
  authorInitials,
  MISSING,
  relativeTimeLabel,
  messageTimeLabel,
  postDateLabel,
} from "@bw/shared/components";
import type {
  ConversationResponse,
  ConversationParticipantSummary,
  MessageResponse,
  PlayerCard as PlayerCardData,
} from "@bw/shared/types/generated";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import { useConversations } from "../hooks/useConversations";
import { useMessages } from "../hooks/useMessages";
import { useObservedReadBoundary } from "../hooks/useObservedReadBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../common/query/queryClient";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { usePlayerSearch } from "../hooks/usePlayerSearch";
import { useCreateConversation } from "../hooks/useCreateConversation";
import { useScrollToConfirmedMessage } from "../hooks/useScrollToConfirmedMessage";
import { ListSkeleton } from "./ListSkeleton";
import { SharedPostEmbed } from "./SharedPostEmbed";
import { MessageDeleteAction } from "./MessageDeleteAction";
import { MessageAttachments } from "./MessageAttachments";
import { MessageComposer } from "./MessageComposer";
import { conversationPreview } from "../lib/conversationPreview";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { BlockPlayerButton } from "../../../common/components/BlockPlayerButton";
import { ReportDialog } from "../../../common/components/ReportDialog";
import { initials } from "../../../common/avatar";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

/**
 * The full-workspace TWO-PANE messaging surface. ONE component backs BOTH routes:
 *   - /community/messages              → `activeId` undefined (InboxPage)
 *   - /community/messages/:id          → `activeId` set       (ThreadPage)
 *
 * Layout (driven by `useIsNarrow`, which defaults to DESKTOP under jsdom/SSR):
 *   DESKTOP — the bounded conversation LIST (left) is ALWAYS mounted; the
 *     fill-width right pane is the open THREAD when `activeId` is set, else a
 *     "Select a conversation" placeholder.
 *   MOBILE  — single column: the LIST when there's no `activeId`; the THREAD
 *     (with a back affordance → /community/messages) REPLACING the list when one is
 *     open. The desktop placeholder is suppressed (it would be dead UI).
 *
 * a11y: the surface owns EXACTLY ONE page <h1> directly above the workspace. The
 * thread pane is a labelled role="region" naming the counterpart — NOT a second
 * <h1> (WCAG 1.3.1 / 2.4.6).
 *
 * THIN presentation: every field is the server's enriched ConversationResponse /
 * MessageResponse rendered VERBATIM. The only view-side computations are the
 * counterpart pick (user_id !== me), chronological reverse, date-divider grouping,
 * and placement of the server's viewer-relative `is_own`/aggregate "Seen" facts — none
 * is business logic, and NONE invents presence ("online"/"typing"/"last seen").
 */

/** A neutral, non-identifying header title when the conversation is not in the
 *  inbox cache (a cold deep-link). NEVER the route conversation_id uuid. */
const UNKNOWN_THREAD_TITLE = "Conversation";
export function MessagesLayout({ activeId }: { activeId?: string }) {
  // The page is gated on auth (AuthGuard → RequireProfile), so a user is present;
  // `me` is used only to pick the counterpart; message ownership is already the
  // server's viewer-relative `is_own` projection. Never
  // hardcoded; undefined only in the brief auth-loading window (handled honestly).
  const { user } = useAuth();
  const me = user?.uid;

  const isNarrow = useIsNarrow();

  const conversationsQ = useConversations();
  const conversations: ConversationResponse[] = conversationsQ.data ?? [];
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [search, setSearch] = useState("");
  // Client-side narrowing of the loaded inbox: unread, and a name/preview match.
  const visibleConversations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return conversations.filter((conversation) => {
      if (filter === "unread" && conversation.unread_count <= 0) return false;
      if (!needle) return true;
      const names = counterparts(conversation.participants, me)
        .map((person) => person.display_name ?? "")
        .join(" ");
      const preview = conversationPreview(conversation.last_message) ?? "";
      return `${names} ${preview}`.toLowerCase().includes(needle);
    });
  }, [conversations, filter, search, me]);

  // The active conversation (for the thread header + the 1:1 "Seen" gate), resolved
  // from the inbox cache — there is NO getConversation(id) endpoint. A cold
  // deep-link (id not in the cache) yields undefined → the neutral title + no
  // "Seen" gate, never the uuid.
  const activeConversation = useMemo(
    () => conversations.find((c) => c.conversation_id === activeId),
    [conversations, activeId],
  );

  // Layout decisions. On desktop BOTH panes can show; on mobile only one.
  const showList = !isNarrow || !activeId;
  const showThread = Boolean(activeId);
  // The desktop empty-right-pane placeholder: only when no conversation is open
  // AND we're on desktop (a second pane exists). Suppressed in single-column.
  const showPlaceholder = !activeId && !isNarrow;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        height: {
          xs: "calc(100dvh - 8.75rem - env(safe-area-inset-bottom, 0px))",
          sm: "100%",
        },
      }}
    >
      <Typography component="h1" sx={visuallyHidden}>Messages</Typography>

      <Box
        component="section"
        role="region"
        aria-label="Messages workspace"
        data-testid="messages-workspace"
        data-layout={isNarrow ? "mobile" : "desktop"}
        sx={{
          display: "grid",
          gridTemplateColumns: isNarrow
            ? "minmax(0, 1fr)"
            : "minmax(288px, 360px) minmax(0, 1fr)",
          flex: 1,
          minHeight: 0,
          borderTop: 1,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {showList ? (
          <Box
            data-testid="conversation-rail"
            data-width="bounded"
            sx={{
              minWidth: 0,
              overflow: "auto",
              borderRight: isNarrow ? 0 : 1,
              borderColor: "divider",
            }}
          >
            {/* The "New message" affordance lives on the conversation-list pane
                (always mounted on the list route + on desktop). It is gated on a
                signed-in `me` — it drives a per-user write (createConversation),
                so a signed-out tree never surfaces it. */}
            <Stack spacing={1} sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <TextField
                  type="search"
                  size="small"
                  fullWidth
                  placeholder="Search messages"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  slotProps={{
                    htmlInput: { "aria-label": "Search messages" },
                    input: {
                      sx: { borderRadius: 999 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {me ? <ComposeMessageLauncher me={me} /> : null}
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Chip
                  label="All"
                  size="small"
                  color={filter === "all" ? "primary" : "default"}
                  variant={filter === "all" ? "filled" : "outlined"}
                  aria-pressed={filter === "all"}
                  onClick={() => setFilter("all")}
                />
                <Chip
                  label="Unread"
                  size="small"
                  color={filter === "unread" ? "primary" : "default"}
                  variant={filter === "unread" ? "filled" : "outlined"}
                  aria-pressed={filter === "unread"}
                  onClick={() => setFilter("unread")}
                />
                <Chip
                  component={RouterLink}
                  to="/community/inquiries"
                  label="Service inquiries"
                  size="small"
                  variant="outlined"
                  clickable
                />
              </Stack>
            </Stack>
            <ConversationList
              conversationsQ={conversationsQ}
              conversations={visibleConversations}
              filtered={filter !== "all" || Boolean(search.trim())}
              me={me}
              activeId={activeId}
            />
          </Box>
        ) : null}

        {showThread ? (
          <ThreadPane
            key={activeId as string}
            conversationId={activeId as string}
            conversation={activeConversation}
            me={me}
            isNarrow={isNarrow}
          />
        ) : showPlaceholder ? (
          <Stack
            data-testid="thread-workspace"
            data-width="fill"
            role="region"
            aria-label="Conversation workspace"
            sx={{
              minWidth: 0,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Select a conversation
            </Typography>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

// =============================================================================
// Compose a NEW message — the "New message" launcher + the player-search dialog
// =============================================================================

/** Debounce window (ms) before a typed query re-fires the player search — avoids
 *  a per-keystroke fetch race; the most recent settled value is what's searched.
 *  Mirrors PartnerFinderDialog / FriendsPage. */
const COMPOSE_SEARCH_DEBOUNCE_MS = 250;

/**
 * A small local debounce. There is no shared useDebouncedValue hook (FriendsPage /
 * PartnerFinderDialog each keep their own); this keeps the value stable for
 * COMPOSE_SEARCH_DEBOUNCE_MS after the last change so usePlayerSearch fires once on
 * the settled query, not on every keystroke. usePlayerSearch's OWN `enabled` gate
 * (≥2 chars) then decides whether to hit the network — a 1-char / blank query never
 * fires (the server requires q min_length=2 and 422s on a shorter value).
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * The "New message" launcher on the conversation-list pane — a compact button that
 * opens the compose dialog. Rendered ONLY for a signed-in user (the caller gates on
 * `me`); `me` is threaded into the dialog so a stray self-row can be excluded.
 */
function ComposeMessageLauncher({ me }: { me: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <IconButton
        type="button"
        aria-label="New message"
        title="New message"
        onClick={() => setOpen(true)}
      >
        <EditOutlinedIcon />
      </IconButton>
      {open ? (
        <ComposeMessageDialog me={me} onClose={() => setOpen(false)} />
      ) : null}
    </Box>
  );
}

/**
 * The compose-NEW-message dialog (Slice S4c). A WAI-ARIA modal (role=dialog,
 * aria-modal, an accessible name, Escape/scrim/Cancel → onClose) housing a debounced
 * player search → the server's PlayerCard[] rendered VERBATIM → a per-card "Message"
 * action that get-or-creates the 1:1 conversation and navigates to the RETURNED,
 * SERVER conversation_id (never the selected player's user_id).
 *
 * THIN presentation: the search is the server's truth (usePlayerSearch → the
 * PlayerCard[] rendered with no client sort / derived rating / fabricated
 * relationship), and the create→navigate target is the server's returned
 * conversation_id — never an optimistic phantom, never the user_id.
 *
 * Self-exclusion: a stray self-row (user_id === me) is filtered out of the results,
 * so createConversation([me]) can never be fired (defensive — the server already
 * self-excludes by scope).
 *
 * Failure honesty: a create rejection surfaces a role="alert", KEEPS the dialog open,
 * and does NOT navigate (no half-open state).
 */
function ComposeMessageDialog({
  me,
  onClose,
}: {
  me: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  // The user_id of the result currently being created — drives the per-card
  // busy/disabled latch so only the clicked row shows "Starting…", and a pending
  // create on row A never freezes row B.
  const [creatingUserId, setCreatingUserId] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query, COMPOSE_SEARCH_DEBOUNCE_MS);
  // usePlayerSearch bakes the ≥2-char `enabled` gate + the blank-guard over
  // socialApi.searchPlayers; scope "friends" asks the server for eligible DM recipients.
  const searchQ = usePlayerSearch(debouncedQuery, "friends");
  const createConversation = useCreateConversation();

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the search input on open (WAI-ARIA modal — focus moves into the dialog).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clear the per-card latch once a create settles.
  useEffect(() => {
    if (!createConversation.isPending) setCreatingUserId(null);
  }, [createConversation.isPending]);

  // Navigate to the created/returned conversation's thread, GUARDED against a
  // malformed conversation_id (a backend contract regression). The backend
  // get-or-create for a 1:1 always returns a real uuid; an empty/whitespace id is
  // NOT a usable route, so we navigate ONLY on a non-empty trimmed string id
  // (mirrors FriendsPage.goToConversation). The SELECTED player's user_id is NEVER
  // a nav target — we navigate to the SERVER's conversation_id.
  function goToConversation(conversation: ConversationResponse) {
    const id = conversation.conversation_id;
    if (typeof id === "string" && id.trim()) {
      navigate(`/community/messages/${id}`);
    }
  }

  function handleSelect(userId: string) {
    // Self-guard (defense in depth — self is already filtered from the rows): never
    // get-or-create a self-conversation.
    if (userId === me) return;
    // The latch: while a create is in flight a second click is a no-op, so
    // createConversation fires EXACTLY once.
    if (createConversation.isPending) return;
    setCreatingUserId(userId);
    // The clicked row's user_id rides this call (per-row scoped) — NOT the first
    // result's. Navigate ONLY on success, to the RETURNED conversation_id.
    createConversation.mutate(userId, { onSuccess: goToConversation });
  }

  // Self-exclusion: drop any stray self-row so it is never a selectable result and
  // createConversation([me]) can never fire.
  const results: PlayerCardData[] = (searchQ.data ?? []).filter(
    (p) => p.user_id !== me,
  );

  const searchActive = debouncedQuery.trim().length >= 2;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={createConversation.isPending ? undefined : onClose}
      aria-labelledby="compose-message-title"
    >
      <DialogTitle id="compose-message-title" sx={{ pr: 7 }}>
        New message
        <IconButton
          type="button"
          size="small"
          onClick={onClose}
          disabled={createConversation.isPending}
          aria-label="Close"
          title="Close"
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* ONE player-search input. The query is debounced; usePlayerSearch's own
            ≥2-char gate decides whether to hit the network — a 1-char / blank query
            never fires a search. */}
          <Stack
            component="form"
            role="search"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              inputRef={inputRef}
              autoFocus
              type="search"
              label="Search friends by name"
              placeholder="Search friends by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
            />
          </Stack>

          {!searchActive ? (
            // Honest idle prompt for a blank / too-short query — never a fabricated
            // "0 results", never a sent q="".
            <Typography color="text.secondary">
              Search by name to start a new message.
            </Typography>
          ) : searchQ.isError ? (
            <Alert severity="error" role="alert">
              We couldn&apos;t search right now. Please try again.
            </Alert>
          ) : searchQ.isPending || searchQ.isFetching ? (
            <Typography color="text.secondary" role="status" aria-live="polite">
              Searching…
            </Typography>
          ) : results.length === 0 ? (
            <Typography color="text.secondary">No friends found.</Typography>
          ) : (
            <List disablePadding>
              {results.map((player) => {
                const isCreating =
                  createConversation.isPending &&
                  creatingUserId === player.user_id;
                return (
                  <MessagePlayerRow
                    key={player.user_id}
                    player={player}
                    disabled={createConversation.isPending}
                    busy={isCreating}
                    onSelect={() => handleSelect(player.user_id)}
                  />
                );
              })}
            </List>
          )}

          {/* ONE create-error surface. A rejected create (403 blocked / 500 /
            network) is told to the player HONESTLY in an assertive region; the
            dialog STAYS open and NO navigation happens (no half-open state). */}
          {createConversation.isError ? (
            <GuardianAwareError error={createConversation.error}>
              <Alert severity="error" role="alert">
                We couldn&apos;t start that conversation. Please try again.
              </Alert>
            </GuardianAwareError>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function MessagePlayerRow({
  player,
  disabled,
  busy,
  onSelect,
}: {
  player: PlayerCardData;
  disabled: boolean;
  busy: boolean;
  onSelect: () => void;
}) {
  return (
    <ListItem divider sx={{ px: 2 }}>
      <MaterialPlayerCard
        userId={player.user_id}
        displayName={player.display_name}
        avatarUrl={player.avatar_url}
        rating={player.rating}
        ratingDivision={player.rating_division}
        relationship={player.relationship}
        actions={
          <Button
            type="button"
            variant="contained"
            size="small"
            onClick={onSelect}
            disabled={disabled}
            aria-busy={busy || undefined}
          >
            {busy ? "Starting…" : "Message"}
          </Button>
        }
      />
    </ListItem>
  );
}

// =============================================================================
// Left pane — the conversation list
// =============================================================================

/**
 * The OTHER participants — everyone whose user_id !== me. The ONLY allowed
 * client-side derivation: it never invents a participant, it filters the server's
 * own list. A self-only conversation yields an empty list (the caller then renders
 * an em-dash / the neutral title, never my own identity).
 */
function counterparts(
  participants: ConversationParticipantSummary[],
  me: string | undefined,
): ConversationParticipantSummary[] {
  return participants.filter((p) => p.user_id !== me);
}

/**
 * The counterpart NAME(S) for a conversation, as a renderable node:
 *   - no viewer / no counterpart        → the neutral placeholder (never my own
 *     identity, never the conversation_id uuid),
 *   - exactly ONE counterpart (a 1:1)   → their display_name + the `#abcd` handle
 *     (last-4 of user_id — the same identity every PlayerCard / the leaderboard
 *     shows), so two same-named DMs are distinguishable. The handle rides a
 *     RESOLVED name only (authorLabel degrades an absent / id-equals-name value to
 *     the em-dash, which carries NO handle — the raw uid never leaks),
 *   - a GROUP (2+ counterparts)         → the joined display_names (no per-name
 *     disc — a list of "Ann #ab12, Bob #cd34" is noisy; the group title stays the
 *     plain joined names).
 *
 * Shared by the inbox row + the thread header so both surfaces show the SAME
 * identity. `fallback` is the placeholder used when there is no resolvable
 * counterpart (the row passes the em-dash; the header passes its "Conversation"
 * title).
 */
function CounterpartLabel({
  others,
  hasViewer,
  fallback,
  discAriaHidden = false,
}: {
  others: ConversationParticipantSummary[];
  hasViewer: boolean;
  fallback: string;
  /**
   * When this label is the target of an `aria-labelledby` (the thread header), set
   * true so the visual `#abcd` disc is hidden from the accessible name — the region
   * then announces the clean counterpart name ("Bella Other"), not "Bella Other
   * #be11". The disc is a VISUAL disambiguator; a screen reader already reads the
   * full name, so the 4-char hint is redundant noise in the a11y tree. The inbox
   * row (not an a11y label target) leaves it false so the disc is announced inline.
   */
  discAriaHidden?: boolean;
}) {
  if (!hasViewer || others.length === 0) return <>{fallback}</>;
  if (others.length === 1) {
    const p = others[0];
    const name = authorLabel(p?.display_name, p?.user_id);
    return (
      <>
        {name}
        {name !== MISSING && p?.user_id ? (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            aria-hidden={discAriaHidden || undefined}
          >
            {" "}
            {playerHandleSuffix(p.user_id)}
          </Typography>
        ) : null}
      </>
    );
  }
  return (
    <>{others.map((p) => authorLabel(p.display_name, p.user_id)).join(", ")}</>
  );
}

function ConversationList({
  conversationsQ,
  conversations,
  filtered = false,
  me,
  activeId,
}: {
  conversationsQ: ReturnType<typeof useConversations>;
  conversations: ConversationResponse[];
  /** The list is narrowed by a filter or search (changes the empty copy). */
  filtered?: boolean;
  me: string | undefined;
  activeId?: string;
}) {
  // The alert wins ONLY on an errored query with NO cached data (the first-load
  // failure). The inbox POLLS every 30s — a transient background refetch rejection
  // flips isError true while the last-good `data` is still cached; gating the alert
  // on `isError` alone would blank a perfectly readable inbox on a routine network
  // blip. Showing the list whenever data is present keeps the readable inbox up;
  // the first-load case (data === undefined) still alerts.
  if (conversationsQ.isError && !conversationsQ.data) {
    return (
      <Alert severity="error" role="alert">
        Couldn&apos;t load your messages. Please try again.
      </Alert>
    );
  }
  if (conversationsQ.isLoading) {
    return <ListSkeleton />;
  }
  if (conversations.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          {filtered ? "No matching conversations" : "No conversations yet"}
        </Typography>
      </Box>
    );
  }
  return (
    <List disablePadding>
      {conversations.map((conversation) => (
        <ConversationRow
          key={conversation.conversation_id}
          conversation={conversation}
          me={me}
          active={conversation.conversation_id === activeId}
        />
      ))}
    </List>
  );
}

/**
 * One conversation row — a real <Link> to the thread by conversation_id (the uuid
 * is the link TARGET only, never rendered as visible body text). Shows the
 * counterpart label, an optional avatar, the last-message preview, a relative-time
 * chip, and the unread badge (strictly from unread_count).
 */
function ConversationRow({
  conversation,
  me,
  active,
}: {
  conversation: ConversationResponse;
  me: string | undefined;
  active: boolean;
}) {
  const others = useMemo(
    () => counterparts(conversation.participants, me),
    [conversation.participants, me],
  );

  // The avatar shows only in the 1:1 case (a group has no single face); the server
  // image when present, otherwise a neutral decorative placeholder.
  const avatarSrc = me && others.length === 1 ? others[0]?.avatar_url : null;

  // The preview line: real text verbatim, "Shared a post" for a shared-post-only
  // last message (empty text + has_shared_post), else null → "No messages yet".
  // ONE pure derivation shared with the MessagingDock's list row (#1841).
  const preview = conversationPreview(conversation.last_message);
  const time = relativeTimeLabel(conversation.last_message_at);
  const showUnread = conversation.unread_count > 0;

  const oneName = me && others.length === 1 ? others[0]?.display_name : null;
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={RouterLink}
        to={`/community/messages/${conversation.conversation_id}`}
        selected={active}
        aria-current={active ? "true" : undefined}
        sx={{ py: 1, px: 1.5, gap: 1.5 }}
      >
        <Avatar
          src={avatarSrc ?? undefined}
          alt=""
          aria-hidden
          sx={{ width: 48, height: 48, fontSize: 17, fontWeight: 700 }}
        >
          {oneName ? initials(oneName) : null}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "space-between", alignItems: "baseline" }}
          >
            <Typography
              component="span"
              noWrap
              sx={{ fontWeight: showUnread ? 800 : 600, minWidth: 0 }}
            >
              {/* Defensive: without `me` the user_id !== me filter is a no-op, so
                `others` would be the WHOLE participant list and a 1:1 would
                wrongly show my own name. AuthGuard blocks an unauthed mount, but
                guard honestly — no `me` → the em-dash placeholder (hasViewer
                false), never my identity. */}
              <CounterpartLabel
                others={others}
                hasViewer={Boolean(me)}
                fallback={MISSING}
              />
            </Typography>
            {time ? (
              <Typography
                component="span"
                variant="caption"
                color={showUnread ? "text.primary" : "text.secondary"}
                sx={{ flexShrink: 0 }}
              >
                {time}
              </Typography>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography
              component="span"
              variant="body2"
              color={showUnread ? "text.primary" : "text.secondary"}
              noWrap
              sx={{ flex: 1, minWidth: 0, fontWeight: showUnread ? 600 : 400 }}
            >
              {preview ? preview : "No messages yet"}
            </Typography>
            {showUnread ? (
              <Box
                component="span"
                aria-label={`${conversation.unread_count} unread`}
                sx={{
                  minWidth: 20, height: 20, px: 0.75, borderRadius: 999, flexShrink: 0,
                  display: "inline-grid", placeItems: "center",
                  bgcolor: "primary.main", color: "primary.contrastText",
                  fontSize: "0.75rem", fontWeight: 800,
                }}
              >
                {conversation.unread_count}
              </Box>
            ) : null}
          </Stack>
        </Box>
      </ListItemButton>
    </ListItem>
  );
}

/**
 * A small list-row avatar (1:1 rows only). Renders the server image when present;
 * otherwise a neutral decorative placeholder circle. `alt` is empty (decorative) —
 * the adjacent display_name is the accessible label — and the placeholder is
 * aria-hidden, so it adds no text and leaks no raw id. Mirrors FriendsPage.
 */
// =============================================================================
// Right pane — the open thread
// =============================================================================

/**
 * The header counterpart(s) for the route conversation, resolved from the inbox
 * cache: the 1:1 person (display_name + `#abcd` handle), the joined group names,
 * or the neutral placeholder for a cold deep-link / no viewer. Returns the
 * `others` list + whether a real title is resolvable, so the header can render the
 * SAME `<CounterpartLabel>` the inbox row uses (one identity source). NEVER the
 * route conversation_id uuid.
 */
function resolveHeaderCounterparts(
  conversation: ConversationResponse | undefined,
  me: string | undefined,
): { others: ConversationParticipantSummary[]; hasViewer: boolean } {
  if (!conversation || !me) return { others: [], hasViewer: false };
  return {
    others: counterparts(conversation.participants, me),
    hasViewer: true,
  };
}

/**
 * Submit the exact rendered read boundary. The hook de-duplicates a confirmed
 * boundary across polling/rerenders, retries a failed boundary, and re-arms when
 * a newer rendered message becomes observable.
 */
function ThreadPane({
  conversationId,
  conversation,
  me,
  isNarrow,
}: {
  conversationId: string;
  conversation: ConversationResponse | undefined;
  me: string | undefined;
  isNarrow: boolean;
}) {
  const headerCounterparts = useMemo(
    () => resolveHeaderCounterparts(conversation, me),
    [conversation, me],
  );
  // A 1:1 thread names the other player (composer, block / report menu).
  const direct =
    headerCounterparts.hasViewer && headerCounterparts.others.length === 1
      ? headerCounterparts.others[0]
      : undefined;
  const directName = direct ? authorLabel(direct.display_name, direct.user_id) : null;
  const usableDirectName = directName && directName !== MISSING ? directName : null;
  return (
    <MessageThread
      conversationId={conversationId}
      authenticated={Boolean(me)}
      isNarrow={isNarrow}
      headingLevel="h2"
      backTo="/community/messages"
      backLabel="Messages"
      writeAccess={conversation?.write_access}
      composerPlaceholder={usableDirectName ? `Message ${usableDirectName}` : undefined}
      headerActions={
        direct && usableDirectName ? (
          <DirectThreadMenu playerId={direct.user_id} playerName={usableDirectName} />
        ) : undefined
      }
      title={
        <CounterpartLabel
          others={headerCounterparts.others}
          hasViewer={headerCounterparts.hasViewer}
          fallback={UNKNOWN_THREAD_TITLE}
          discAriaHidden
        />
      }
    />
  );
}

export function MessageThread({
  conversationId,
  title,
  headingLevel,
  backTo,
  backLabel,
  writeAccess,
  readOnlyReason,
  authenticated,
  isNarrow,
  communityId,
  headerActions,
  subtitle,
  composerPlaceholder,
  canModerateMessages = false,
  canDeleteOwnMessages = true,
  onMessageConfirmed,
  initialDraft,
  timelineItems = [],
  focusTimelineItemId,
}: {
  conversationId: string;
  title: ReactNode;
  headingLevel: "h1" | "h2";
  backTo: string;
  backLabel: string;
  writeAccess: ConversationResponse["write_access"] | undefined;
  readOnlyReason?: "awaiting_staff_reply";
  authenticated: boolean;
  isNarrow: boolean;
  communityId?: string;
  headerActions?: ReactNode;
  /** One quiet line under the title ("Smash City · 12 members"). */
  subtitle?: ReactNode;
  composerPlaceholder?: string;
  canModerateMessages?: boolean;
  canDeleteOwnMessages?: boolean;
  onMessageConfirmed?: () => void;
  initialDraft?: string;
  timelineItems?: Array<{
    id: string;
    occurredAt: string;
    content: ReactNode;
  }>;
  focusTimelineItemId?: string;
}) {
  const messagesQ = useMessages(conversationId);
  const queryClient = useQueryClient();
  const markRead = useObservedReadBoundary(
    conversationId,
    messagesQ.readMessageBatches,
    authenticated && !messagesQ.isError,
    messagesQ.refreshReadAuthorityBatch,
    messagesQ.revokeAuthority,
    communityId,
  );
  useEffect(() => {
    if (communityId && (messagesQ.isError || markRead.isError)) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.communityChannels(communityId),
      });
    }
  }, [communityId, messagesQ.isError, markRead.isError, queryClient]);

  // Pages are NEWEST-first from the server; reverse the deduplicated history for
  // chronological display.
  const items: MessageResponse[] = messagesQ.messages;
  const ordered = useMemo(() => [...items].reverse(), [items]);
  const timeline = useMemo(
    () =>
      [
        ...ordered.map((message, position) => ({
          id: `message-${message.message_id}`,
          occurredAt: message.created_at,
          position,
          kind: "message" as const,
          message,
          content: null,
        })),
        ...timelineItems
          .filter((item) => Number.isFinite(Date.parse(item.occurredAt)))
          .map((item, index) => ({
            ...item,
            position: ordered.length + index,
            kind: "timeline" as const,
            message: null,
          })),
      ].sort((left, right) => {
        const byTime = Date.parse(left.occurredAt) - Date.parse(right.occurredAt);
        return byTime || left.position - right.position;
      }),
    [ordered, timelineItems],
  );
  const focusedTimelineItem = useRef<string | null>(null);
  useEffect(() => {
    if (!focusTimelineItemId || focusedTimelineItem.current === focusTimelineItemId ||
        !timeline.some((item) => item.id === focusTimelineItemId) || messagesQ.isLoading) return;
    const target = document.getElementById(focusTimelineItemId);
    if (!target) return;
    focusedTimelineItem.current = focusTimelineItemId;
    target.scrollIntoView?.({ block: "center" });
    target.focus({ preventScroll: true });
  }, [focusTimelineItemId, messagesQ.isLoading, timeline]);
  const { scrollContainerRef, onMessageSent } = useScrollToConfirmedMessage(
    items.map((message) => message.message_id),
  );

  // The id of MY latest message — the only bubble that may carry "Seen". The server
  // page is newest-first, so my first own-message in `items` is my latest.
  const myLatestId = useMemo(
    () => items.find((message) => message.is_own)?.message_id ?? null,
    [items],
  );

  // A stable label for the region + the labelledby target id.
  const labelId = useId();

  return (
    <Stack
      component="section"
      role="region"
      aria-labelledby={labelId}
      data-testid="thread-workspace"
      data-width="fill"
      sx={{ height: "100%", minWidth: 0, minHeight: 0, overflow: "hidden" }}
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{
          alignItems: "center",
          minHeight: 56,
          // The bar keeps arrow + title on one line; manager actions wrap below.
          flexWrap: "wrap",
          px: { xs: 1, sm: 3 },
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* The back-link returns to the list route. On MOBILE it is the ONLY way
            back (the thread replaces the list); on DESKTOP the list is already
            beside the thread, so the link is styled as a compact, secondary
            affordance rather than a primary nav. It is always present so a
            keyboard / screen-reader user has an explicit path back, and so the
            single-page thread contract (a back-link to /community/messages) holds. */}
        <IconButton
          component={RouterLink}
          to={backTo}
          edge="start"
          aria-label={
            backLabel.startsWith("Back to ")
              ? backLabel
              : `Back to ${backLabel.toLowerCase()}`
          }
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
        <Typography
          id={labelId}
          component={headingLevel}
          sx={{ fontSize: "1.0625rem", fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}
        >
          {communityId ? (
            <Box component="span" aria-hidden sx={{ color: "text.secondary", mr: 0.5 }}>
              #
            </Box>
          ) : null}
          {/* This span is the region's aria-labelledby target → hide the visual
              `#abcd` disc from the accessible name (the SR reads the clean
              counterpart name; the disc is a visual-only disambiguator). */}
          {title}
        </Typography>
        {subtitle ? (
          <Typography noWrap variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
        </Box>
        {headerActions}
      </Stack>

      {/* The alert wins ONLY on a first-load failure (isError with NO cached
          data). The thread POLLS — a transient background refetch rejection flips
          isError true while the last-good messages are still cached; gating on
          isError alone would blank a readable thread on a network blip. */}
      {messagesQ.isError && !messagesQ.data ? (
        <Alert severity="error" role="alert" sx={{ m: 2 }}>
          We couldn&apos;t load this conversation. Please try again.
        </Alert>
      ) : messagesQ.isLoading ? (
        <Typography color="text.secondary" role="status" sx={{ p: 2 }}>
          Loading messages…
        </Typography>
      ) : timeline.length === 0 ? (
        <Stack
          sx={{ flex: 1, alignItems: "center", justifyContent: "center", p: 2 }}
        >
          <Typography color="text.secondary">
            No messages yet. Say hello.
          </Typography>
        </Stack>
      ) : (
        <Box
          component="ol"
          ref={scrollContainerRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            listStyle: "none",
            m: 0,
            px: { xs: 1.5, sm: 3 },
            py: 2,
          }}
        >
          {messagesQ.hasNextPage ? (
            <Box
              component="li"
              sx={{ display: "flex", justifyContent: "center", mb: 2 }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled={messagesQ.isFetchingNextPage}
                onClick={() => void messagesQ.fetchNextPage()}
              >
                {messagesQ.isFetchingNextPage
                  ? "Loading…"
                  : "Load earlier messages"}
              </Button>
            </Box>
          ) : null}
          {messagesQ.isFetchNextPageError ? (
            <Box component="li" sx={{ mb: 2 }}>
              <Alert
                severity="error"
                action={
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => void messagesQ.fetchNextPage()}
                  >
                    Retry
                  </Button>
                }
              >
                We couldn&apos;t load earlier messages.
              </Alert>
            </Box>
          ) : null}
          {timeline.map((item, index) => {
            const prev = index > 0 ? timeline[index - 1] : null;
            // A day divider precedes a message on a different calendar day than the
            // one before it (and before the very first message).
            const divider = dayDivider(prev?.occurredAt, item.occurredAt);
            return (
              <ThreadGroup key={item.id} divider={divider}>
                {item.kind === "message" && item.message ? (
                  <MessageBubble
                    variant={communityId ? "channel" : "direct"}
                    continued={
                      !divider &&
                      prev?.kind === "message" &&
                      Boolean(prev.message) &&
                      continuesSender(prev.message!, item.message)
                    }
                    message={item.message}
                    conversationId={conversationId}
                    communityId={communityId}
                    canModerateMessages={canModerateMessages}
                    canDeleteOwnMessages={canDeleteOwnMessages}
                    showSeen={
                      item.message.message_id === myLatestId &&
                      item.message.seen_by_others === true &&
                      !item.message.is_deleted
                    }
                  />
                ) : (
                  <Box
                    component="li"
                    id={item.id}
                    tabIndex={-1}
                    data-testid="conversation-timeline-item"
                    sx={{ mx: 1, my: 1.5, scrollMarginTop: 80 }}
                  >
                    {item.content}
                  </Box>
                )}
              </ThreadGroup>
            );
          })}
        </Box>
      )}

      {markRead.isError ? (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button size="small" color="inherit" onClick={markRead.retry}>
              Retry
            </Button>
          }
          sx={{ mx: 2, mb: 1 }}
        >
          We couldn&apos;t update your read status.
        </Alert>
      ) : null}

      <MessageComposer
        initialDraft={initialDraft}
        conversationId={conversationId}
        communityId={communityId}
        disabled={!authenticated || messagesQ.isError}
        writeAccess={writeAccess}
        readOnlyReason={readOnlyReason}
        placeholder={composerPlaceholder}
        onMessageSent={(message) => {
          onMessageSent(message);
          onMessageConfirmed?.();
        }}
      />
    </Stack>
  );
}

/**
 * The date-divider label between two messages, or null when they share a calendar
 * day (so no divider renders). Date-ONLY via postDateLabel (no time of day, no raw
 * ISO). The first message always gets a divider (prevIso is undefined).
 */
function dayDivider(
  prevIso: string | null | undefined,
  iso: string | null | undefined,
): string | null {
  const day = calendarDay(iso);
  if (day === null) return null; // unparseable → no fabricated divider
  const prevDay = calendarDay(prevIso);
  if (prevDay === day) return null; // same day → no divider
  const now = new Date();
  if (day === calendarDay(now.toISOString())) return "Today";
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 12);
  if (day === calendarDay(yesterday.toISOString())) return "Yesterday";
  return postDateLabel(iso);
}

/** The YYYY-MM-DD calendar day of an ISO instant (viewer-local), or null. */
function calendarDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Whether `next` continues `prev`'s run: same sender, within five minutes.
 * Messages carry no sender id, so a run is "both mine", or "both from the same
 * named sender with the same face". A continued message stacks under the first
 * (no repeated face or name), like the approved channel view.
 */
function continuesSender(prev: MessageResponse, next: MessageResponse): boolean {
  if (prev.is_deleted || next.is_deleted) return false;
  const gap = Date.parse(next.created_at) - Date.parse(prev.created_at);
  if (!Number.isFinite(gap) || gap < 0 || gap > 5 * 60_000) return false;
  if (prev.is_own || next.is_own) return prev.is_own === next.is_own;
  return (
    Boolean(prev.sender_display_name) &&
    prev.sender_display_name === next.sender_display_name &&
    (prev.sender_avatar_url ?? null) === (next.sender_avatar_url ?? null)
  );
}

/** A divider (when present) followed by the bubble. The divider is a list item so
 *  the <ol> stays valid; it is decorative-text (a formatted date), never an id. */
function ThreadGroup({
  divider,
  children,
}: {
  divider: string | null;
  children: React.ReactNode;
}) {
  return (
    <>
      {divider ? (
        <Box component="li" aria-hidden="false" sx={{ my: 2 }}>
          <Divider sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
            {divider}
          </Divider>
        </Box>
      ) : null}
      {children}
    </>
  );
}

/**
 * One message bubble. `data-own` exposes the server's viewer-relative `is_own`
 * projection for alignment + styling; no sender identifier reaches this view.
 *
 * An OTHER-sender bubble shows a counterpart FACE: the server `sender_avatar_url`
 * as an <img alt={name}> (a non-empty accessible label that is NEVER the uid), or
 * an initials chip via authorInitials(sender_display_name) when there's no url. It
 * also labels the sender by display_name (em-dash when absent — NEVER the uid).
 *
 * A MINE bubble carries NO counterpart face, NO author label, and never my uid.
 * The aggregate "Seen" fact renders only beneath my latest message.
 *
 * The note is React-escaped and passed through `linkifyContent`, which turns any
 * genuinely-typed http/https URL into a real <a> element while leaving everything
 * else — including `javascript:` / `data:` "urls" — as inert escaped text (rendered
 * only when non-empty). A SHARED post rides as a first-class `SharedPostEmbed` BELOW
 * the note (never a URL woven into the text), so a bubble may show both. NO
 * dangerouslySetInnerHTML; message_id is control data only, never visible text.
 */
function MessageBubble({
  message,
  conversationId,
  communityId,
  canModerateMessages,
  canDeleteOwnMessages,
  showSeen,
  variant = "channel",
  continued = false,
}: {
  /** "channel": chat density, name + time inline. "direct": bubbles, yours lime. */
  variant?: "channel" | "direct";
  /** The same sender's previous message is right above: no repeated face/name. */
  continued?: boolean;
  message: MessageResponse;
  conversationId: string;
  communityId?: string;
  canModerateMessages: boolean;
  canDeleteOwnMessages: boolean;
  showSeen: boolean;
}) {
  const isOwn = message.is_own;
  const time = messageTimeLabel(message.created_at);
  const name = isOwn ? "You" : authorLabel(message.sender_display_name);
  const direct = variant === "direct";
  // In a direct thread your own messages are lime bubbles on the right; the
  // header already names the other person.
  const showName = !continued && !(direct && isOwn);
  const showFace = !isOwn && !continued;

  return (
    <Stack
      component="li"
      direction={direct && isOwn ? "row-reverse" : "row"}
      spacing={1}
      data-testid="message-row"
      data-own={isOwn ? "true" : "false"}
      data-continued={continued ? "true" : undefined}
      sx={{
        position: "relative",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: 1,
        px: 1,
        py: continued ? 0.25 : 0.75,
        mb: 0.25,
        borderRadius: 1,
        "&:hover": { bgcolor: "action.hover" },
        "&:hover [data-testid='message-actions'], &:focus-within [data-testid='message-actions']":
          {
            opacity: 1,
          },
      }}
    >
      {showFace ? (
        <BubbleAvatar
          src={message.sender_avatar_url}
          name={name}
          displayName={message.sender_display_name}
        />
      ) : direct && isOwn ? null : (
        <Box aria-hidden="true" sx={{ width: 32, flex: "0 0 32px" }} />
      )}
      <Box
        data-testid="message-content"
        sx={{
          minWidth: 0,
          maxWidth: direct ? "min(80%, 560px)" : "min(100%, 760px)",
          overflowWrap: "anywhere",
          ...(direct
            ? {
                px: 1.5,
                py: 0.75,
                borderRadius: "16px",
                ...(isOwn
                  ? { bgcolor: "primary.main", color: "primary.contrastText" }
                  : { bgcolor: "action.hover" }),
              }
            : {}),
        }}
      >
        <Stack spacing={0.5}>
          {showName ? (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline" }}>
              <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: "0.8125rem" }}
              >
                {name}
              </Typography>
              {time ? (
                <Typography
                  component="span"
                  variant="caption"
                  color={direct && isOwn ? "inherit" : "text.secondary"}
                >
                  {time}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            // The sender (and time) stay available to screen readers.
            <Box component="span" sx={visuallyHidden}>
              {time ? `${name}, ${time}` : name}
            </Box>
          )}
          {/* The typed note — React-escaped text with http/https URLs linkified into
            real <a> elements (a genuinely-typed link stays clickable). NO
            dangerouslySetInnerHTML; a javascript:/data: "url" stays inert escaped
            text. Rendered only when non-empty so an empty-with-shared-post message
            shows just the embed (no blank line). */}
          {message.is_deleted ? (
            <Typography
              component="span"
              sx={{ fontStyle: "italic", opacity: 0.8 }}
            >
              Message deleted
            </Typography>
          ) : message.content ? (
            <Typography component="span" sx={{ whiteSpace: "pre-wrap" }}>
              {linkifyContent(message.content)}
            </Typography>
          ) : null}
          {!message.is_deleted ? (
            <MessageAttachments attachments={message.attachments} />
          ) : null}
          {/* A SHARED post rides as a first-class embed BELOW the note (a message may
            carry BOTH a note AND an embed). Renders nothing when there is no shared
            post; an honest tombstone when the original is unavailable. */}
          {!message.is_deleted ? <SharedPostEmbed message={message} /> : null}
          {/* "Seen" only beneath MY latest, 1:1, read-by-the-other message. */}
          {isOwn && showSeen ? (
            <Typography
              component="span"
              variant="caption"
              sx={{ opacity: 0.8, textAlign: direct ? "right" : undefined }}
            >
              Seen
            </Typography>
          ) : null}
        </Stack>
      </Box>
      {!message.is_deleted &&
      ((isOwn && canDeleteOwnMessages) || canModerateMessages) ? (
        <MessageActions
          conversationId={conversationId}
          communityId={communityId}
          messageId={message.message_id}
        />
      ) : null}
    </Stack>
  );
}

/**
 * Slack-style message controls belong beside a message, not inside the readable
 * body. The compact toolbar appears on hover or keyboard focus (and stays visible
 * on touch devices); deleting still requires the existing confirmation dialog.
 */
function MessageActions({
  conversationId,
  communityId,
  messageId,
}: {
  conversationId: string;
  communityId?: string;
  messageId: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();

  return (
    <Box
      data-testid="message-actions"
      sx={{
        alignSelf: "flex-start",
        mt: 0.25,
        opacity: { xs: 1, sm: 0 },
        transition: "opacity 120ms ease-out",
        "@media (hover: none)": { opacity: 1 },
      }}
    >
      <IconButton
        type="button"
        size="small"
        aria-label="Message actions"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="menu"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreHorizRoundedIcon fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        keepMounted
      >
        <MessageDeleteAction
          conversationId={conversationId}
          communityId={communityId}
          messageId={messageId}
          variant="menuitem"
          onDialogOpen={() => setAnchorEl(null)}
        />
      </Menu>
    </Box>
  );
}

/**
 * The counterpart's face on an other-sender bubble. The server image when present
 * → an <img alt={name}> (a non-empty accessible label, NEVER the uid). No url → an
 * initials chip via authorInitials (the counterpart's initials, never the uid);
 * when there is no usable name the chip is a neutral, text-free circle. NEVER uses
 * dangerouslySetInnerHTML, so a hostile display_name can never inject an element.
 */
function BubbleAvatar({
  src,
  name,
  displayName,
}: {
  src: string | null | undefined;
  name: string;
  displayName: string | null | undefined;
}) {
  const initials = authorInitials(displayName);
  return (
    <Avatar
      src={src ?? undefined}
      alt={src ? name : ""}
      aria-hidden={src ? undefined : true}
      sx={{ width: 32, height: 32, fontSize: "0.75rem" }}
    >
      {initials}
    </Avatar>
  );
}

// =============================================================================
// Safe linkifier — turn http/https URLs in a message into real <a> elements
// =============================================================================

/**
 * Matches a run that STARTS with the http/https scheme, up to the next whitespace.
 * This is the ONLY thing linkified: `javascript:` / `data:` / `mailto:` / a bare
 * word never begin with `http(s)://`, so they never match and stay inert escaped
 * text — the security guarantee lives in the pattern itself, not a denylist.
 */
const URL_RE = /https?:\/\/[^\s]+/gi;

/** Trailing sentence punctuation peeled off a matched url (so a period / closing
 *  bracket at the end of a sentence isn't swallowed into the href). */
const TRAILING_PUNCT_RE = /[.,;:!?)\]}'"]+$/;

/** ASCII whitespace + C0 controls + DEL — stripped before the scheme check so an
 *  obfuscated `java\tscript:`-style split can never masquerade as http(s). */
const CONTROL_RE = new RegExp("[\\u0000-\\u0020\\u007F]", "g");

/**
 * Return a SAFE http/https href, or null. Defense-in-depth on top of URL_RE: strip
 * control/space chars, lowercase, and require the http(s):// scheme on the collapsed
 * form (a browser collapses embedded controls before resolving the scheme). Anything
 * else → null → rendered as inert text.
 */
function safeHttpUrl(candidate: string): string | null {
  if (typeof candidate !== "string") return null;
  const stripped = candidate.replace(CONTROL_RE, "");
  const scheme = stripped.toLowerCase();
  if (scheme.startsWith("http://") || scheme.startsWith("https://")) {
    return stripped;
  }
  return null;
}

/**
 * Split `text` on http/https URL matches and map each safe url to a real
 * `<a target="_blank" rel="noopener noreferrer">` — building REACT ELEMENTS, never
 * an HTML string, so there is no dangerouslySetInnerHTML and every non-url segment
 * is React-escaped text. A match that fails `safeHttpUrl` (or any non-http scheme,
 * which never matches URL_RE in the first place) renders as inert text.
 *
 * Applies to EVERY bubble — a URL in any message becomes a link — which is the
 * intended chat behaviour.
 */
export function linkifyContent(text: string): React.ReactNode {
  if (typeof text !== "string" || text.length === 0) return text;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  URL_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = URL_RE.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;

    // Peel trailing sentence punctuation off the url so it stays visible text.
    const punct = TRAILING_PUNCT_RE.exec(raw);
    const trailing = punct ? punct[0] : "";
    const urlText = trailing ? raw.slice(0, raw.length - trailing.length) : raw;

    // Emit the literal run before this match (React-escaped).
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

    const href = safeHttpUrl(urlText);
    if (href) {
      nodes.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
          {urlText}
        </a>,
      );
    } else {
      // Should not happen for a URL_RE match, but stay safe: inert text.
      nodes.push(urlText);
    }
    if (trailing) nodes.push(trailing);

    lastIndex = start + raw.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  // A no-url message collapses back to the plain string (so tests / callers that
  // read the bubble's text node see the content verbatim).
  if (nodes.length === 0) return text;
  if (nodes.length === 1 && typeof nodes[0] === "string") return nodes[0];
  return nodes;
}

/**
 * The 1:1 thread's ⋯: block the other player or report them — the same
 * controls (and the same server checks) as their profile's options.
 */
function DirectThreadMenu({ playerId, playerName }: { playerId: string; playerName: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <IconButton
        ref={moreRef}
        aria-label="Conversation options"
        aria-haspopup="true"
        aria-expanded={anchor ? "true" : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        edge="end"
      >
        <MoreHorizRoundedIcon />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack spacing={1} sx={{ p: 1.5, alignItems: "stretch", minWidth: 200 }}>
          <BlockPlayerButton playerId={playerId} playerName={playerName} onBlocked={() => setAnchor(null)} />
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => {
              setAnchor(null);
              setReportOpen(true);
            }}
          >
            Report
          </Button>
        </Stack>
      </Popover>
      <ReportDialog
        open={reportOpen}
        subjectLabel={playerName}
        targetId={playerId}
        targetType="player"
        onClose={() => {
          setReportOpen(false);
          requestAnimationFrame(() => moreRef.current?.focus());
        }}
      />
    </>
  );
}
